import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { EAuthType } from '@onesource/schemas';
import { generateOttMutationFn, useOttSsoHandler } from '@onesource/services';
import { LinkProps, useSearch } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { FC, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const queryCheckSchema = z.object({
  token: z.string().min(1),
  redirectTo: z.string().optional(),
  auth_type: z.nativeEnum(EAuthType).optional(),
  return_url: z.string().optional(),
  return_app: z.string().optional(),
});

const DELAY = 1500;

export const SsoCheck: FC = () => {
  const search = useSearch({ from: '/auth/sso' });
  const [tokenState, setIsTokenState] = useState<'ERROR' | 'SUCCESS' | 'INITIAL'>('INITIAL');
  const [errorMessage, setErrorMessage] = useState<string>('Token invalid, redirecting to login');
  const validate = queryCheckSchema.safeParse(search);
  const isValidatingRef = useRef(false);

  const { handleOttSso } = useOttSsoHandler();

  useEffect(() => {
    if (isValidatingRef.current || tokenState !== 'INITIAL') {
      return;
    }

    if (!validate.success) {
      setIsTokenState('ERROR');
      return;
    }

    isValidatingRef.current = true;
    handleOtt(validate.data.token, validate.data.redirectTo, validate.data.return_url, validate.data.return_app);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOtt = async (token: string, redirectTo?: string, returnUrl?: string, returnApp?: string) => {
    try {
      await handleOttSso(token);
      setIsTokenState('SUCCESS');

      if (returnUrl && returnApp) {
        try {
          const decodedReturnUrl = decodeURIComponent(returnUrl);
          const data = await generateOttMutationFn({
            targetApp: returnApp,
            redirectUrl: decodedReturnUrl,
          });
          window.location.href = data.redirectUrl;
          return;
        } catch (error) {
          console.error('[SSO Return Redirect] Failed to generate OTT for target app:', error);
          // fall through to default redirect on failure
        }
      }

      setTimeout(() => {
        const destination: LinkProps['to'] | (string & {}) = redirectTo || '/';
        window.location.href = destination;
      }, 200);
    } catch (error: unknown) {
      const axiosErr = error instanceof AxiosError ? error : null;
      const message = error instanceof Error ? error.message : 'Authentication failed';
      setErrorMessage(axiosErr?.response?.data?.message || message);
      setIsTokenState('ERROR');
    }
  };

  useEffect(() => {
    if (tokenState === 'ERROR') {
      setTimeout(function () {
        const loginPage: LinkProps['to'] = '/auth/login';
        window.location.href = loginPage;
      }, DELAY);
      return;
    }
  }, [tokenState, validate.data]);

  return (
    <Container
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100dvh - 439px)',
      }}
    >
      <Box sx={{ flexGrow: 1, textAlign: 'center', maxWidth: '400px' }}>
        <LinearProgress sx={{ width: '100%' }} />
        {tokenState === 'INITIAL' && <Typography mt={3}>Checking...</Typography>}
        {tokenState === 'SUCCESS' && <Typography mt={3}>Success! redirecting ...</Typography>}
        {tokenState === 'ERROR' && <Typography mt={3}>{errorMessage}</Typography>}
      </Box>
    </Container>
  );
};
